import React, { useCallback, useEffect, useRef } from "react";
import DrayageContainer from "./DrayageContainer";

// Module-level flags to prevent duplicate API calls across all component instances
// These persist across component mounts/unmounts and multiple instances
let hasSentAuthTokenDataGlobally = false;
let hasSetupListenerGlobally = false;
const getInitialWarehouseId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return localStorage.getItem("selectedWarehouses") || localStorage.getItem("selectedWarehouse");
  } catch (error) {
    return null;
  }
};
let lastSentWarehouseId = getInitialWarehouseId();

/**
 * Drayage Container - Bridge component between FleetEnable and Drayage applications
 */
const Drayage = () => {
  const hasSentAuthTokenDataRef = useRef(false);
  const lastWarehouseIdRef = useRef(lastSentWarehouseId);

  const passUserData = async () => {
    if (hasSentAuthTokenDataRef.current) {
      return;
    }
    // Lock immediately to prevent concurrent duplicate calls from rapid DRAYAGE_READY events.
    hasSentAuthTokenDataRef.current = true;
    // Wrapped in a block
    const tokenString = localStorage.getItem("auth");
    const currentUserString = localStorage.getItem("CURRENT_USER");

    if (!tokenString || !currentUserString) {
      // console.error("[Drayage] Missing token or user data in localStorage.");
      return;
    }

    let tokenData;
    let userData;

    try {
      tokenData = JSON.parse(tokenString);
      // tokenData.access_token = "04GHTfKqgMLEVA6zFEoTy4GhI47sebn80QIwPDQmvGc"
    } catch (error) {
      // console.error("Error parsing auth token:", error);
      return;
    }

    try {
      userData = JSON.parse(currentUserString);
    } catch (error) {
      // console.error("Error parsing current user data:", error);
      return;
    }

    // Organization configurations will be fetched by fe-drayage app
    const formattedUserData = {
      id: userData._id,
      firstName: userData?.first_name,
      lastName: userData?.last_name,
      email: userData?.email,
      image: userData?.image?.url,
      role: userData?.roles,
      accounts: userData?.accounts,
      warehouse_ids: userData?.warehouse_ids,
      currentOrg: userData?.currentOrg?._id,
      active_warehouse_ids: userData?.active_warehouse_ids,
      preferences: {
        // Default preferences - fe-drayage will fetch org configurations separately
        is_military_time: false
      }
    };

    // Get warehouse data from localStorage
    const selectedWarehouses = localStorage.getItem("selectedWarehouses");
    const selectedWarehouse = localStorage.getItem("selectedWarehouse");
    const warehouses = localStorage.getItem("warehouses");

    let warehouseData = null;
    let warehouseId = null;
    let isMultiSelect = false;

    // Parse warehouse data from localStorage
    if (selectedWarehouses) {
      try {
        const parsedWarehouses = warehouses ? JSON.parse(warehouses) : [];
        // selectedWarehouses is stored as a plain string ID
        warehouseId = selectedWarehouses;
        warehouseData = parsedWarehouses.find(wh => wh.id === warehouseId);
        isMultiSelect = true;
      } catch (e) {
        // console.error("Error parsing warehouse data from localStorage:", e);
      }
    } else if (selectedWarehouse) {
      try {
        const parsedWarehouses = warehouses ? JSON.parse(warehouses) : [];
        warehouseId = selectedWarehouse;
        warehouseData = parsedWarehouses.find(wh => wh.id === selectedWarehouse);
        isMultiSelect = false;
      } catch (e) {
        // console.error("Error parsing warehouse data from localStorage:", e);
      }
    }

    const data = {
      token: tokenData,
      user: formattedUserData,
      warehouse: warehouseId ? {
        warehouseId,
        warehouseData,
        isMultiSelect
      } : null
    };

    // Track the warehouse ID we're sending to detect changes later
    lastWarehouseIdRef.current = warehouseId;
    lastSentWarehouseId = warehouseId;

    sendDataToDrayage("AUTH_TOKEN_DATA", data);
  };

  // Function to send data to Drayage app
  const sendDataToDrayage = useCallback((messageType = "FLEET_DATA_UPDATE", customData = null) => {
    try {
      // Use passed data if provided, otherwise send empty object
      const data = customData || {};

      // Send to Drayage using custom event - single standard method
      window.dispatchEvent(
        new CustomEvent("fleet-to-drayage", {
          detail: {
            type: messageType,
            data: data,
          },
        })
      );

      return true;
    } catch (error) {
      // console.error("Error sending data to Drayage:", error);
      return false;
    }
  }, []);

  // Listen for messages from Drayage
  useEffect(() => {
    // Prevent multiple event listeners if multiple Drayage components are mounted
    if (hasSetupListenerGlobally) {
      return;
    }
    hasSetupListenerGlobally = true;

    const handleDrayageMessage = event => {
      const { type, data } = event.detail;
      // Handle specific message types
      switch (type) {
        case "DRAYAGE_READY":
          if (!hasSentAuthTokenDataGlobally) {
            hasSentAuthTokenDataRef.current = false;
            hasSentAuthTokenDataGlobally = true;
            passUserData();
          }
          break;

        case "DRAYAGE_REQUEST_DATA":
          if (data?.dataType === "fleetStats") {
            sendDataToDrayage();
          }
          break;

        case "ACTION_COMPLETED":
          break;

        case "ACTION_ERROR":
          // console.error(`Drayage action error: ${data.error}`);
          break;

        case "PREFERENCES_UPDATE":
          break;

        default:
          // Optional fallback
          break;
      }
    };

    // Listen for messages from Drayage
    window.addEventListener("drayage-to-fleet", handleDrayageMessage);

    return () => {
      window.removeEventListener("drayage-to-fleet", handleDrayageMessage);
      hasSetupListenerGlobally = false;
      hasSentAuthTokenDataGlobally = false;
      lastSentWarehouseId = getInitialWarehouseId();
    };
  }, [sendDataToDrayage]);

  // Watch for warehouse changes and send updated data to Drayage
  useEffect(() => {
    const checkWarehouseChange = () => {
      const selectedWarehouses = localStorage.getItem("selectedWarehouses");
      const selectedWarehouse = localStorage.getItem("selectedWarehouse");

      const currentWarehouseId = selectedWarehouses || selectedWarehouse;

      if (!hasSentAuthTokenDataGlobally && !hasSentAuthTokenDataRef.current) {
        passUserData();
        return;
      }

      if (currentWarehouseId && currentWarehouseId !== lastWarehouseIdRef.current) {
        hasSentAuthTokenDataGlobally = false;
        hasSentAuthTokenDataRef.current = false;

        passUserData();
      }
    };

    // Check on mount (in case warehouse changed while away)
    checkWarehouseChange();

    // Listen for storage events (cross-tab changes)
    const handleStorageChange = (e) => {
      if (e.key === "selectedWarehouses" || e.key === "selectedWarehouse") {
        checkWarehouseChange();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also poll for changes (same-tab warehouse changes don't trigger storage events)
    const intervalId = setInterval(checkWarehouseChange, 2000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  return <DrayageContainer />;
};

export default Drayage;
